import { motion } from "framer-motion";
import { Skull, Ghost, CloudFog, Flame } from "lucide-react";
import { CountdownTimer } from "./CountdownTimer";
import { Cobweb } from "./Cobweb";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskedLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pr-1 -mr-1">
    <motion.span
      custom={i}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ event, artists = [] }) => {
  return (
    <section className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden bg-black grain">
      <div className="absolute inset-0 tile-grid opacity-60" />
      <div className="absolute inset-0 radial-fade-acid" />
      <div className="absolute inset-0 radial-fade-blood" />

      <Cobweb className="absolute top-0 left-0 w-28 h-28 sm:w-40 sm:h-40 text-white/40 z-10" />

      <motion.div
        animate={{ x: [0, 40, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-10 left-1/4 text-white/5"
      >
        <CloudFog size={220} strokeWidth={0.6} />
      </motion.div>
      <motion.div
        animate={{ x: [0, -50, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 right-1/4 text-white/5"
      >
        <CloudFog size={260} strokeWidth={0.6} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="absolute top-24 right-6 sm:right-10 hidden sm:block text-acid/70"
      >
        <Skull size={34} strokeWidth={1.2} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, rotate: -8 }}
        animate={{ opacity: 1, rotate: 0 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="absolute bottom-10 right-10 sm:right-20 hidden sm:block text-blood/60"
      >
        <Ghost size={28} strokeWidth={1.2} />
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-6 pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-eyebrow"
          className="font-mono text-xs sm:text-sm uppercase tracking-[0.35em] text-acid mb-4 sm:mb-6"
        >
          {event.eyebrow} · {event.city} · {event.dateLabel}
        </motion.p>

        <h1
          className="font-display font-extrabold uppercase leading-[0.9] tracking-tight sm:tracking-tighter text-[8.5vw] sm:text-6xl lg:text-7xl text-foreground"
          data-testid="hero-title"
        >
          <MaskedLine i={0}>{event.title}</MaskedLine>
          <MaskedLine i={1} className="text-outline-acid">
            {event.city}
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-lineup-names"
          className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-display font-bold uppercase tracking-wide text-sm sm:text-base"
        >
          <Flame size={14} className="text-blood shrink-0" />
          {artists.map((a, i) => (
            <span key={a.id} className="flex items-center gap-3">
              <span className="text-foreground">{a.name}</span>
              {i < artists.length - 1 && <span className="text-blood/70">·</span>}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 max-w-lg text-base sm:text-lg text-zinc-400 font-body"
        >
          {event.tagline} One night only, dressed for the occult.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 sm:mt-12"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-blood/80 mb-3 flex items-center gap-2">
            <Skull size={12} /> Countdown to the Haunting
          </p>
          <CountdownTimer targetISO={event.dateISO} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href={event.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-get-tickets-btn"
            className="inline-flex items-center gap-3 bg-acid text-black font-display font-extrabold uppercase tracking-wide text-sm sm:text-base px-7 sm:px-9 py-4 hover:bg-white transition-colors duration-300 animate-pulse-glow"
          >
            Get Tickets
          </a>
          <span className="font-mono text-sm text-zinc-500">
            {event.priceLabel} · {event.venueName}
          </span>
        </motion.div>
      </div>
    </section>
  );
};
